/**
 * Color Map Utilities
 *
 * Centralized color mapping utilities for consistent UI styling across
 * the application. Eliminates DRY violations by providing a single source
 * of truth for status, role, and percentage-based color schemes.
 *
 * @see DRY compliance: Replaces duplicate implementations in:
 * - settings/users/page.tsx (getRoleBadgeColor, statusColors)
 * - settings/users/components/role-selector.tsx (getRoleBadgeColor)
 * - admin/tenants/page.tsx (statusColors, statusLabels)
 * - settings/subscription/components/usage-meter.tsx (getProgressColor)
 * - settings/users/components/usage-card.tsx (getProgressColor)
 */

// ═══════════════════════════════════════════════════════════
// TYPE DEFINITIONS
// ═══════════════════════════════════════════════════════════

/**
 * Chakra UI color palette names for badges and status indicators.
 * Note: This type is also exported from badge.tsx - keep them in sync (DRY).
 */
export type BadgeColorPalette =
  | 'gray'
  | 'brand'
  | 'green'
  | 'yellow'
  | 'red'
  | 'blue'
  | 'orange'
  | 'purple';

/**
 * Hex colors for progress bars and custom indicators.
 */
export type HexColor = string;

// ═══════════════════════════════════════════════════════════
// ROLE COLOR MAPPING
// ═══════════════════════════════════════════════════════════

/**
 * Role to badge color mapping.
 * Use with Chakra UI Badge colorPalette prop.
 */
const ROLE_COLORS: Record<string, BadgeColorPalette> = {
  super_admin: 'red',
  tenant_admin: 'purple',
  manager: 'blue',
  operator: 'green',
  driver: 'gray',
  support: 'orange',
};

/**
 * Get badge color for a user role.
 *
 * @param role - User role string (e.g., 'tenant_admin', 'manager')
 * @returns Badge color palette name
 *
 * @example
 * <Badge colorPalette={getRoleBadgeColor('manager')}>Manager</Badge>
 */
export function getRoleBadgeColor(role: string): BadgeColorPalette {
  return ROLE_COLORS[role] ?? 'gray';
}

// ═══════════════════════════════════════════════════════════
// USER STATUS COLOR MAPPING
// ═══════════════════════════════════════════════════════════

/**
 * User status to badge color mapping.
 */
const USER_STATUS_COLORS: Record<string, BadgeColorPalette> = {
  active: 'green',
  inactive: 'gray',
  pending: 'yellow',
  suspended: 'red',
};

/**
 * Get badge color for a user status.
 *
 * @param status - User status string (e.g., 'active', 'pending')
 * @returns Badge color palette name
 *
 * @example
 * <Badge colorPalette={getUserStatusColor('active')}>Active</Badge>
 */
export function getUserStatusColor(status: string): BadgeColorPalette {
  return USER_STATUS_COLORS[status] ?? 'gray';
}

// ═══════════════════════════════════════════════════════════
// TENANT STATUS COLOR MAPPING
// ═══════════════════════════════════════════════════════════

/**
 * Tenant status to badge color mapping.
 */
const TENANT_STATUS_COLORS: Record<string, BadgeColorPalette> = {
  active: 'green',
  trial: 'yellow',
  suspended: 'red',
  cancelled: 'gray',
};

/**
 * Get badge color for a tenant status.
 *
 * @param status - Tenant status string (e.g., 'active', 'trial')
 * @returns Badge color palette name
 *
 * @example
 * <Badge colorPalette={getTenantStatusColor('trial')}>Trial</Badge>
 */
export function getTenantStatusColor(status: string): BadgeColorPalette {
  return TENANT_STATUS_COLORS[status] ?? 'gray';
}

// ═══════════════════════════════════════════════════════════
// INVITATION STATUS COLOR MAPPING
// ═══════════════════════════════════════════════════════════

/**
 * Invitation status to badge color mapping.
 */
const INVITATION_STATUS_COLORS: Record<string, BadgeColorPalette> = {
  PENDING: 'yellow',
  ACCEPTED: 'green',
  EXPIRED: 'gray',
  REVOKED: 'red',
};

/**
 * Get badge color for an invitation status.
 *
 * @param status - Invitation status string (e.g., 'PENDING', 'ACCEPTED')
 * @returns Badge color palette name
 *
 * @example
 * <Badge colorPalette={getInvitationStatusColor('PENDING')}>Pending</Badge>
 */
export function getInvitationStatusColor(status: string): BadgeColorPalette {
  return INVITATION_STATUS_COLORS[status] ?? 'gray';
}

// ═══════════════════════════════════════════════════════════
// PERCENTAGE/PROGRESS COLOR MAPPING
// ═══════════════════════════════════════════════════════════

/**
 * Progress bar threshold hex colors.
 */
export const PROGRESS_COLORS = {
  green: '#10B981',
  yellow: '#F59E0B',
  red: '#EF4444',
} as const;

/**
 * Get progress bar color based on percentage.
 * Uses thresholds: <75% = green, >=75% = yellow, >=90% = red
 *
 * @param percent - Usage percentage (0-100)
 * @param isUnlimited - If true, always returns green
 * @returns Badge color palette name ('green' | 'yellow' | 'red')
 *
 * @example
 * const colorPalette = getProgressColor(85); // 'yellow'
 */
export function getProgressColor(percent: number, isUnlimited = false): BadgeColorPalette {
  if (isUnlimited) return 'green';
  if (percent >= 90) return 'red';
  if (percent >= 75) return 'yellow';
  return 'green';
}

/**
 * Get progress bar hex color based on percentage.
 * Uses thresholds: <75% = green, >=75% = yellow, >=90% = red
 *
 * @param percent - Usage percentage (0-100)
 * @param isUnlimited - If true, always returns green
 * @returns Hex color string
 *
 * @example
 * <Progress.Range backgroundColor={getProgressHexColor(85)} /> // #F59E0B (yellow)
 */
export function getProgressHexColor(percent: number, isUnlimited = false): HexColor {
  if (isUnlimited) return PROGRESS_COLORS.green;
  if (percent >= 90) return PROGRESS_COLORS.red;
  if (percent >= 75) return PROGRESS_COLORS.yellow;
  return PROGRESS_COLORS.green;
}

/**
 * Get text color based on percentage for usage displays.
 * Uses thresholds: <75% = default, >=75% = yellow, >=90% = red
 *
 * @param percent - Usage percentage (0-100)
 * @param isUnlimited - If true, always returns green
 * @returns Hex color string for text color
 *
 * @example
 * <Text color={getProgressTextColor(95)}>95%</Text> // red text
 */
export function getProgressTextColor(percent: number, isUnlimited = false): HexColor {
  if (isUnlimited) return PROGRESS_COLORS.green;
  if (percent >= 90) return PROGRESS_COLORS.red;
  if (percent >= 75) return PROGRESS_COLORS.yellow;
  return '#374151'; // Default gray text
}

// ═══════════════════════════════════════════════════════════
// REMAINING COUNT COLOR MAPPING
// ═══════════════════════════════════════════════════════════

/**
 * Get text color based on remaining count.
 *
 * @param remaining - Number of items remaining
 * @param warningThreshold - Threshold for warning color (default: 2)
 * @returns Hex color string
 *
 * @example
 * <Text color={getRemainingColor(1)}>1 remaining</Text> // yellow text
 * <Text color={getRemainingColor(0)}>0 remaining</Text> // red text
 */
export function getRemainingColor(remaining: number, warningThreshold = 2): HexColor {
  if (remaining <= 0) return PROGRESS_COLORS.red;
  if (remaining <= warningThreshold) return PROGRESS_COLORS.yellow;
  return '#6B7280'; // Default muted gray
}

// ═══════════════════════════════════════════════════════════
// UNIFIED COLOR MAP OBJECT
// ═══════════════════════════════════════════════════════════

/**
 * Unified ColorMap object for convenient access to all color utilities.
 *
 * @example
 * import { ColorMap } from '../index';
 *
 * // Role colors
 * <Badge colorPalette={ColorMap.role('manager')}>Manager</Badge>
 *
 * // Status colors
 * <Badge colorPalette={ColorMap.userStatus('active')}>Active</Badge>
 *
 * // Progress colors
 * <Progress.Range backgroundColor={ColorMap.progressHex(85)} />
 */
export const ColorMap = {
  role: getRoleBadgeColor,
  userStatus: getUserStatusColor,
  tenantStatus: getTenantStatusColor,
  invitationStatus: getInvitationStatusColor,
  progress: getProgressColor,
  progressHex: getProgressHexColor,
  progressText: getProgressTextColor,
  remaining: getRemainingColor,
} as const;
